

import React, { useState, useEffect, captureOwnerStack } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  fetchPosts,
  fetchNotifications,
  savePost,
  updatePost,
} from "../../features/admin/PostsSlice";
import TextEditor from "../TextEditor";
import { toast } from "react-toastify";
import { convertToSlug } from "../../utils/helps";
import ApiCustom from "../../utils/ApiCustom";
import { formatDateReverse } from "../../utils/helps";
import DatePicker from "../DatePicker";

const postSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(5, "Tiêu đề phải ít nhất 5 ký tự")
    .required("Tiêu đề không được để trống"),

  content: Yup.string()
    .trim()
    .test("content-length", "Nội dung không được để trống", (value) => {
      return value && value.trim().length >= 10;
    })
    .required("Nội dung không được để trống"),
  eventStartTime: Yup.date()
    .required("Ngày diễn ra hoạt động không được để trống")
    .min(
      new Date().toISOString().slice(0, 10),
      "Ngày diễn ra hoạt động phải từ hôm nay trở đi"
    )
    .typeError("Ngày diễn ra hoạt động không hợp lệ"),
  regisStartTime: Yup.date()
    .required("Ngày bắt đầu đăng ký không được để trống")
    .max(
      Yup.ref("eventStartTime"),
      "Ngày bắt đầu đăng ký phải trước ngày diễn ra"
    )
    .typeError("Ngày bắt đầu đăng ký không hợp lệ"),
  regisEndTime: Yup.date()
    .required("Ngày bắt đầu đăng ký không được để trống")
    .max(
      Yup.ref("eventStartTime"),
      "Ngày kết thúc đăng ký phải trước ngày diễn ra"
    )
    .min(
      Yup.ref("regisStartTime"),
      "Ngày kết thúc đăng ký phải sau ngày bắt đầu đăng ký"
    )
    .typeError("Ngày kết thúc đăng ký không hợp lệ"),
});

const NotiModal = ({ postId, onClose, canUpdate = true }) => {
  const [errors, setErrors] = useState({});
  const { categories } = useSelector((state) => state.categories);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isToast, setIsToast] = useState(false);
  const [post, setPost] = useState(null);
  const [logoPreview, setLogoPreview] = useState(post?.images[0]?.url || "");

  const [formData, setFormData] = useState({
    title: post?.title || "",
    content: post?.content || null,
    status: "draft",
    seoTitle: post?.seoTitle || "",
    seoDescription: post?.seoDescription || "",
    seoKeywords: post?.seoKeywords || "",
    cateGory: post?.cateGory || "",
    images: null,
    categoryId: post?.categoryId || "",
    type: "notification",
    eventStartTime: post?.eventStartTime || "",
    regisStartTime: post?.regisStartTime || "",
    regisEndTime: post?.regisEndTime || "",
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (postId) {
      getPosts();
    }
  }, []);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post?.title || "",
        content: post?.content || null,
        status: "draft",
        seoTitle: post?.seoTitle || "",
        seoDescription: post?.seoDescription || "",
        seoKeywords: post?.seoKeywords || "",
        cateGory: post?.cateGory || "",
        images: null,
        categoryId: post?.categoryId || "",
        type: "notification",
        eventStartTime: post?.eventStartTime || "",
        regisStartTime: post?.regisStartTime || "",
        regisEndTime: post?.regisEndTime || "",
      });
    }
  }, [post]);

  const getPosts = () => {
    ApiCustom.get(`/posts/${postId}`)
      .then((res) => {
        setPost(res.data?.data);
      })
      .catch((err) => console.log(err));
  };

  const handleChange = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    const files = e.target?.files;
    let newValue = name === "images" && files ? files[0] : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    try {
      if (
        !["eventStartTime", "regisStartTime", "regisEndTime"].includes(name)
      ) {
        await Yup.reach(postSchema, name).validate(newValue);
      }
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));

      if (name === "images" && newValue instanceof File) {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
        if (!allowedTypes.includes(newValue.type)) {
          setErrors((prev) => ({
            ...prev,
            logo: "Chỉ chấp nhận file ảnh (JPEG, PNG, GIF)",
          }));
          return;
        }

        if (newValue.size > 5 * 1024 * 1024) {
          setErrors((prev) => ({
            ...prev,
            images: "Kích thước file không được vượt quá 5MB",
          }));
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(newValue);
      }
    } catch (validationError) {
      setErrors((prev) => ({
        ...prev,
        [name]: validationError.message,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const finalFormData = {
        ...formData,
      };
      let validationSchema = postSchema;

      if (post?.id && !formData.images) {
        validationSchema = postSchema.omit(["images"]);
      }
      await validationSchema.validate(finalFormData, { abortEarly: false });
      const submitData = new FormData();
      Object.keys(finalFormData).forEach((key) => {
        const value = finalFormData[key];
        if (value !== null && value !== undefined) {
          submitData.append(key, value);
        }
      });

      submitData.append("slug", convertToSlug(finalFormData?.title));

      try {
        setIsToast(false);

        if (post?.id) {
          const updateResult = await dispatch(
            updatePost({ id: post.id, newPost: submitData })
          );
          if (updatePost.fulfilled.match(updateResult)) {
            toast.success("Cập nhật thành công!");
            onClose();
          } else {
            toast.error("Cập nhật thất bại!");
          }
        } else {
          await ApiCustom({
            method: "post",
            url: "blog-posts-create",
            data: submitData,
            // headers: { "Content-Type": "application/json" },
          })
            .then((res) => {
              toast(res.data.message || "Thêm thành công", { type: "success" });
              onClose();
              setTimeout(() => {
                dispatch(fetchNotifications());
              }, 1000);
            })
            .catch((err) => {
              toast(err.response?.data?.messages.slug || "Đã xảy ra lỗi", {
                type: "error",
              });
            });
        }
      } catch (error) {
        setIsToast(false);

        console.error("Error submitting post:", error);
        toast.error("Có lỗi xảy ra!");
      } finally {
        setIsSubmitting(false);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError && error.inner) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      }
      setIsSubmitting(false);
    }
  };
  // Determine if button should be in loading state
  // const isSubmitting = isSubmitting === "loading";

  const categorySort = categories
    ?.slice()
    .sort((a, b) => a.priority - b.priority);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-[9999]">
      <div className="bg-base-100 rounded-lg w-3/4 max-h-[90vh] overflow-y-auto">
        <div className="flex sticky top-0 bg-base-100 z-10 p-6 justify-between items-center">
          <h2 className="text-xl font-semibold mb-4">
            {canUpdate ? (post ? "Chỉnh sửa" : "Thêm") : "Chi tiết"} thông báo
          </h2>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            disabled={isSubmitting}
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6">
            <div className="mb-4">
              <label htmlFor="title" className="block text-sm font-medium">
                Tiêu đề<i className="text-red-600"> *</i>
              </label>
              <input
                type="text"
                name="title"
                disabled={!canUpdate}
                value={formData.title}
                onChange={handleChange}
                className={`w-full p-2 border rounded ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
            <div>
              <div className="gap-2 mb-5 lg:hidden">
                <div className="w-full">
                  <label className="block text-sm font-medium mb-2">
                    Thời gian diễn ra<i className="text-red-600"> *</i>
                  </label>
                  <DatePicker
                    disabled={!canUpdate}
                    name="eventStartTime"
                    value={formData["eventStartTime"]}
                    onChange={(name, value) => {
                      handleChange({
                        target: {
                          name,
                          value,
                        },
                      });
                    }}
                    title="Thời gian diễn ra"
                    error={errors["eventStartTime"]}
                  />
                  {errors.eventStartTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.eventStartTime}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mb-5">
                <div className="w-full hidden lg:block">
                  <label className="block text-sm font-medium mb-2">
                    Thời gian diễn ra<i className="text-red-600"> *</i>
                  </label>
                  <DatePicker
                    disabled={!canUpdate}
                    name="eventStartTime"
                    value={formData["eventStartTime"]}
                    onChange={(name, value) => {
                      handleChange({
                        target: {
                          name,
                          value,
                        },
                      });
                    }}
                    title="Thời gian diễn ra"
                    error={errors["eventStartTime"]}
                  />
                  {errors.eventStartTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.eventStartTime}
                    </p>
                  )}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium mb-2">
                    Bắt dầu đăng ký<i className="text-red-600"> *</i>
                  </label>
                  <DatePicker
                    disabled={!canUpdate}
                    name="regisStartTime"
                    value={formData["regisStartTime"]}
                    onChange={(name, value) => {
                      handleChange({
                        target: {
                          name,
                          value,
                        },
                      });
                    }}
                    title="Bắt dầu đăng ký"
                    error={errors["regisStartTime"]}
                  />

                  {errors.regisStartTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.regisStartTime}
                    </p>
                  )}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium mb-2">
                    Kết thúc đăng ký<i className="text-red-600"> *</i>
                  </label>
                  <DatePicker
                    disabled={!canUpdate}
                    name="regisEndTime"
                    value={formData["regisEndTime"]}
                    onChange={(name, value) => {
                      handleChange({
                        target: {
                          name,
                          value,
                        },
                      });
                    }}
                    title="Kết thúc đăng ký"
                    error={errors["regisEndTime"]}
                  />

                  {errors.regisEndTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.regisEndTime}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="categoryId" className="block text-sm font-medium">
                Danh mục thông báo<i className="text-red-600"> *</i>
              </label>
              <select
                name="categoryId"
                value={formData.categoryId}
                disabled={!canUpdate}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option key={0} value="">
                  Chọn danh mục
                </option>
                {categorySort.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4 ">
              <label className="block text-sm font-medium mb-2">
                Nội dung thông báo<i className="text-red-600"> *</i>
              </label>

              <TextEditor
                value={formData.content}
                onChange={handleChange}
                name="content"
                canUpdate={canUpdate}
                importCustomInsertImage={true}
              />
              {errors.content && (
                <p className="text-red-500 text-sm mt-1">{errors.content}</p>
              )}
            </div>
          </div>

          {canUpdate && (
            <div className="flex border-t sticky bottom-0 bg-base-100 z-10 p-6 justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 btn btn-sm rounded"
                disabled={isSubmitting}
              >
                Hủy
              </button>
              <button
                type="submit"
                className={`px-4 py-2 btn btn-primary rounded btn-sm ${
                  isSubmitting ? "loading" : ""
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-xs mr-2"></span>
                    Đang lưu...
                  </>
                ) : (
                  "Lưu"
                )}
              </button>
            </div>
          )}
          {/* Error Handling */}
          {/* {errors && (
            <p className="text-red-500 text-sm mt-2">
              Đã xảy ra lỗi: {errors.message}
            </p>
          )} */}
        </form>
      </div>
    </div>
  );
};

export default NotiModal;


